import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Overlay full-screen cinematic mostrato quando la sessione MSAL scade,
 * prima del redirect a /login. Comunica all'utente cosa sta succedendo
 * (countdown ring + microcopy rassicurante) ed espone CTA per accelerare
 * il redirect.
 *
 * Mount programmatico via SessionExpiredService (createComponent +
 * attachView). Emette `redirect` al termine del countdown o al click CTA;
 * il caller (AuthSessionService) gestisce la navigazione effettiva.
 */
@Component({
    selector: 'app-session-expired-overlay',
    imports: [TranslateModule],
    templateUrl: './session-expired-overlay.component.html',
    styleUrls: ['./session-expired-overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SessionExpiredOverlayComponent implements OnInit {
  /** Durata countdown in secondi (default 4s). */
  countdownSeconds = input<number>(4);

  /** Emesso quando il countdown raggiunge 0 o l'utente clicca la CTA. */
  redirect = output<void>();

  /** Secondi residui (reattivo). */
  remaining = signal<number>(0);

  /** Circonferenza del ring SVG (radius 42). */
  protected readonly circumference = 2 * Math.PI * 42;

  /** Frazione di countdown trascorsa (0 = inizio, 1 = fine). */
  private readonly progress = computed(() => {
    const total = this.countdownSeconds();
    return total > 0 ? (total - this.remaining()) / total : 1;
  });

  /** stroke-dashoffset bound al progress (offset cresce, ring si svuota). */
  protected readonly ringOffset = computed(() => this.circumference * this.progress());

  private intervalRef: ReturnType<typeof setInterval> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopInterval());
  }

  ngOnInit(): void {
    this.remaining.set(this.countdownSeconds());
    this.intervalRef = setInterval(() => this.tick(), 1000);
  }

  onActionClick(): void {
    this.stopAndEmit();
  }

  private tick(): void {
    const next = this.remaining() - 1;
    if (next <= 0) {
      this.remaining.set(0);
      this.stopAndEmit();
    } else {
      this.remaining.set(next);
    }
  }

  private stopAndEmit(): void {
    this.stopInterval();
    this.redirect.emit();
  }

  private stopInterval(): void {
    if (this.intervalRef !== null) {
      clearInterval(this.intervalRef);
      this.intervalRef = null;
    }
  }
}
